import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ModeloGetUrlJson } from "../ModeloCrud";
import ModalInformation from "../modals/ModalInformation";

/* colores por prepaga */
const prepagaColor = (prepaga) => {
  if (!prepaga) return "#ccc";

  // hash simple desde string
  let hash = 0;
  for (let i = 0; i < prepaga.length; i++) {
    hash = prepaga.charCodeAt(i) + ((hash << 5) - hash);
  }

  // generar HSL pastel
  const h = hash % 360; // tono
  const s = 55; // saturación
  const l = 75; // luminosidad pastel

  return `hsl(${h}, ${s}%, ${l}%)`;
};

const formatDate = (fecha) => {
  const [y, m, d] = fecha.split("-");
  const date = new Date(y, m - 1, d);

  return date.toLocaleDateString("es-AR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export default function AgendaProximosTurnos() {
  const [turnos, setTurnos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalErrorOpen, setModalErrorOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const hideErrorModal = () => {
    setModalMessage(null);
    setModalErrorOpen(false);
  };

  /* llamada API */
  useEffect(() => {
    const fetchTurnos = async () => {
      try {
        setLoading(true);

        let result = await ModeloGetUrlJson(
          "turno",
          null,
          "turnos-proximos-art",
          "",
        );
        if (result.error === "true") {
          setLoading(false);
          setModalMessage(result.mensaje);
          setModalErrorOpen(true);
        } else {
          const data = result.listado;
          setTurnos(data || []);
        }
      } catch (err) {
        console.error("Error cargando turnos ", err);
        setTurnos([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTurnos();
  }, []);

  /* loading */
  if (loading) {
    return (
      <Box
        sx={{
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  /* sin datos */
  if (!turnos.length) {
    return (
      <Box
        sx={{
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="text.secondary">No hay turnos próximos</Typography>
      </Box>
    );
  }

  /* agrupar por fecha */
  const grouped = {};
  turnos.forEach((t) => {
    if (!grouped[t.fecha]) grouped[t.fecha] = [];
    grouped[t.fecha].push(t);
  });

  const fechas = Object.keys(grouped).sort();

  return (
    <Box p={2}>
      <Stack spacing={4}>
        {fechas.map((fecha) => (
          <Box key={fecha}>
            {/* HEADER DIA */}
            <Box
              sx={{
                borderTop: "2px solid #e5e7eb",
                borderBottom: "2px solid #e5e7eb",
                py: 1.5,
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                background: "#f9fafb",
                borderRadius: 1,
                px: 1,
              }}
            >
              <EventIcon sx={{ color: "#2563eb" }} />

              <Typography
                variant="h7"
                sx={{
                  fontWeight: 700,
                  textTransform: "capitalize",
                }}
              >
                {formatDate(fecha)}
              </Typography>
            </Box>

            {/* CARDS */}
            <Stack spacing={2}>
              {grouped[fecha].map((t, i) => (
                <Card
                  key={i}
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                    borderLeft: `5px solid ${prepagaColor(t.prepaga)}`,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Stack spacing={0.5}>
                    {/* FILA 1 → prepaga + hora centrada y alineada */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "140px 1fr 140px",
                        alignItems: "center",
                      }}
                    >
                      {/* prepaga izquierda */}
                      <Box>
                        <Chip
                          label={t.prepaga.toLowerCase()}
                          size="small"
                          sx={{
                            background: prepagaColor(t.prepaga),
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                            textTransform: "capitalize",
                          }}
                        />
                      </Box>

                      {/* hora centro REAL */}
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <AccessTimeIcon
                          sx={{ fontSize: 16, color: "#64748b" }}
                        />

                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 16,
                            color: "#111827",
                            letterSpacing: 0.3,
                          }}
                        >
                          {t.hora}
                        </Typography>
                      </Stack>

                      {/* columna vacía derecha para balance */}
                      <Box />
                    </Box>

                    {/* FILA 2 → paciente */}
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        textTransform: "capitalize",
                        color: "#111827",
                      }}
                    >
                      {t.paciente_nombre.toLowerCase()} · {t.paciente_edad} años
                      · {t.paciente_documento}
                    </Typography>

                    {/* FILA 3 → turno */}
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "primary.main",
                        textTransform: "capitalize",
                      }}
                    >
                      {t.clinica.toLowerCase()} · {t.especialidad.toLowerCase()}{" "}
                      · {t.odontologo.toLowerCase()} ·{" "}
                    </Typography>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
      <ModalInformation
        showModal={modalErrorOpen && modalMessage !== null}
        hideModal={hideErrorModal}
        message={modalMessage}
        tipo="error"
      />
    </Box>
  );
}
